import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import ManagerLayout from "@/components/ManagerLayout";
import { Users, Calendar, Trophy, TrendingUp, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMatchDate } from "@/lib/dates";
import { TeamPhotoCard } from "@/components/TeamPhotoCard";

const ManagerDashboard = () => {
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamRes, playersRes, matchesRes, allTeamsRes] = await Promise.all([
          axios.get(`${API}/manager/my-team`, { withCredentials: true }),
          axios.get(`${API}/manager/my-players`, { withCredentials: true }),
          axios.get(`${API}/manager/my-matches`, { withCredentials: true }),
          axios.get(`${API}/teams`)
        ]);
        setTeam(teamRes.data);
        setPlayers(playersRes.data);
        setMatches(matchesRes.data);
        
        const tMap = {};
        allTeamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeamsMap(tMap);
      } catch (error) {
        console.error("Error fetching manager data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const upcomingMatches = matches.filter(m => m.status === "scheduled").slice(0, 3);
  const recentResults = matches.filter(m => m.status === "completed").slice(0, 3);
  
  // Compute team performance stats
  const teamStats = matches.reduce((acc, m) => {
    if (m.status !== "completed") return acc;
    const isHome = m.home_team_id === team?.team_id;
    const teamScore = isHome ? m.home_score : m.away_score;
    const oppScore = isHome ? m.away_score : m.home_score;
    if (teamScore > oppScore) acc.won++;
    else if (teamScore < oppScore) acc.lost++;
    else acc.drawn++;
    acc.played++;
    return acc;
  }, { played: 0, won: 0, drawn: 0, lost: 0 });

  const formatDate = (d) => formatMatchDate(d, { month: 'short', day: 'numeric' });

  return (
    <ManagerLayout title="Dashboard">
      <div data-testid="manager-dashboard">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : team && (
          <>
            {/* Team Info Card */}
            <div className="bg-white p-8 border border-gray-200 mb-8 flex flex-col md:flex-row items-center gap-6">
              <img 
                src={team.logo_url || `https://placehold.co/120x120/09090b/FFF?text=${team.short_name}`}
                alt={team.name}
                className="w-24 h-24 object-contain"
              />
              <div className="flex-1 text-center md:text-left">
                <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b]">{team.name}</h2>
                <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-2 text-sm text-gray-500">
                  {team.stadium && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {team.stadium}
                    </span>
                  )}
                  {team.founded && <span>Est. {team.founded}</span>}
                  {team.colors && <span>{team.colors}</span>}
                </div>
              </div>
              <Link 
                to={`/teams/${team.team_id}`}
                className="btn-abfa-secondary text-sm"
                data-testid="view-public-profile-btn"
              >
                Public Profile
              </Link>
            </div>

            <TeamPhotoCard team={team} onSaved={setTeam} />

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Squad Size", value: players.length, icon: Users, color: "bg-blue-500" },
                { label: "Matches Played", value: teamStats.played, icon: Calendar, color: "bg-[#FFC72C]" },
                { label: "Wins", value: teamStats.won, icon: Trophy, color: "bg-[#009E60]" },
                { label: "Points", value: teamStats.won * 3 + teamStats.drawn, icon: TrendingUp, color: "bg-purple-500" },
              ].map((stat, i) => (
                <div key={i} className="bg-white p-6 border border-gray-200" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`w-10 h-10 ${stat.color} flex items-center justify-center mb-3`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-heading font-bold text-[#09090b]">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Link to="/manager/players" className="bg-[#FFC72C] p-6 hover:bg-[#E6B325] transition-colors" data-testid="quick-manage-roster">
                <Users className="w-8 h-8 mb-3 text-black" />
                <h3 className="font-heading font-bold uppercase text-black">Manage Roster</h3>
                <p className="text-sm text-black/70 mt-1">Add or update your players</p>
              </Link>
              <Link to="/manager/matches" className="bg-[#009E60] p-6 hover:bg-[#008050] transition-colors text-white" data-testid="quick-manage-matches">
                <Calendar className="w-8 h-8 mb-3" />
                <h3 className="font-heading font-bold uppercase">Enter Match Results</h3>
                <p className="text-sm text-white/80 mt-1">Update scores for your matches</p>
              </Link>
              <Link to={`/teams/${team.team_id}`} className="bg-[#09090b] p-6 hover:bg-gray-800 transition-colors text-white" data-testid="quick-view-team">
                <Trophy className="w-8 h-8 mb-3 text-[#FFC72C]" />
                <h3 className="font-heading font-bold uppercase">View Public Page</h3>
                <p className="text-sm text-white/60 mt-1">See how fans view your team</p>
              </Link>
            </div>

            {/* Upcoming & Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Matches */}
              <div className="bg-white p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg uppercase text-[#09090b]">Upcoming Matches</h3>
                  <Link to="/manager/matches" className="text-sm text-[#009E60] hover:underline">View All</Link>
                </div>
                {upcomingMatches.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingMatches.map(m => {
                      const isHome = m.home_team_id === team.team_id;
                      const opp = teamsMap[isHome ? m.away_team_id : m.home_team_id];
                      return (
                        <Link to={`/matches/${m.match_id}`} key={m.match_id} className="hover:bg-gray-100 transition-colors flex items-center gap-3 p-3 bg-gray-50 border-l-4 border-[#FFC72C]" data-testid={`upcoming-${m.match_id}`}>
                          <span className="text-xs font-bold uppercase w-8 text-center">{isHome ? 'H' : 'A'}</span>
                          <img src={opp?.logo_url} alt="" className="w-8 h-8 object-contain" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{opp?.name}</p>
                            <p className="text-xs text-gray-500">{formatDate(m.match_date)} {m.match_time && `• ${m.match_time}`}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center">No upcoming matches</p>
                )}
              </div>

              {/* Recent Results */}
              <div className="bg-white p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg uppercase text-[#09090b]">Recent Results</h3>
                  <Link to="/manager/matches" className="text-sm text-[#009E60] hover:underline">View All</Link>
                </div>
                {recentResults.length > 0 ? (
                  <div className="space-y-3">
                    {recentResults.map(m => {
                      const isHome = m.home_team_id === team.team_id;
                      const opp = teamsMap[isHome ? m.away_team_id : m.home_team_id];
                      const ourScore = isHome ? m.home_score : m.away_score;
                      const theirScore = isHome ? m.away_score : m.home_score;
                      const result = ourScore > theirScore ? 'W' : ourScore < theirScore ? 'L' : 'D';
                      return (
                        <Link to={`/matches/${m.match_id}`} key={m.match_id} className={`hover:bg-gray-100 transition-colors flex items-center gap-3 p-3 bg-gray-50 border-l-4 ${result === 'W' ? 'border-[#009E60]' : result === 'L' ? 'border-red-500' : 'border-gray-400'}`} data-testid={`result-${m.match_id}`}>
                          <span className={`form-badge form-badge-${result.toLowerCase()}`}>{result}</span>
                          <img src={opp?.logo_url} alt="" className="w-8 h-8 object-contain" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{opp?.name}</p>
                            <p className="text-xs text-gray-500">{formatDate(m.match_date)}</p>
                          </div>
                          <span className="font-heading font-bold text-lg">{ourScore}-{theirScore}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center">No results yet</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </ManagerLayout>
  );
};

export default ManagerDashboard;
